import * as React from "react";
import {CarouselLayout} from "./carousel-layout/CarouselLayout";
import {LoopCarousel} from "./loop-carousel/LoopCarousel";
import {HighlightedButton} from "./carousel-layout/highlighted-button/HighlightedButton";
import ModuleBody from "../../layouts/module-body/ModuleBody";
import {css} from "/src/utils/utils";
import * as styles from "./carousel.module.scss";

const AUTOPLAY_INTERVAL = 5000;

export interface CarouselProps
  extends
    Queries.CarouselFragmentFragment
  {
  mobileLayoutOnly?: boolean;
  darkMode?: boolean;
}

export function Carousel(props: CarouselProps) {
  const {
    alignTitle,
    highlightedButtonShape,
    mobileLayoutOnly,
    autoplay,
    slides,
    title,
    darkMode,
  } = props;

  const autoplayIntervalTimer = React.useRef<NodeJS.Timer>();
  const [selectedSlideIndex, setSelectedSlideIndex] = React.useState(0);

  // on unmount, clear interval
  React.useEffect(() => {
    return () => {
      if (autoplayIntervalTimer.current !== null) {
        clearInterval(autoplayIntervalTimer.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (autoplay && slides && !autoplayIntervalTimer.current) {
      autoplayIntervalTimer.current = setInterval(() => {
        // If on last slide, go to first
        setSelectedSlideIndex(index => (index + 1) % slides.length);
      }, AUTOPLAY_INTERVAL);
    }
  }, [autoplay, slides]);

  const moveSlide = React.useCallback(
    (index: number, disableInterval?: boolean) => {
      setSelectedSlideIndex(index);

      if (disableInterval && autoplayIntervalTimer.current) {
        clearInterval(autoplayIntervalTimer.current);
        autoplayIntervalTimer.current = undefined;
      }
    },
    []
  );

  const handleSlideChange = React.useCallback((newIndex: number) => {
    setSelectedSlideIndex(newIndex);
  }, []);

  if (!slides?.length) {
    return null;
  }

  if (props.useLoopLayout) {
    return <LoopCarousel {...props} />;
  }

  const CarouselContainer = mobileLayoutOnly ? React.Fragment : ModuleBody;
  return (
    <section className={styles.carousel}>
      <CarouselContainer>
        <div
          className={css(
            styles.carousel__content,
            !mobileLayoutOnly ? styles.carousel__contentResponsive : ""
          )}
        >
          <HighlightedButton
            alignTitle={alignTitle}
            selectedSlideIndex={selectedSlideIndex}
            slides={slides}
            onClick={moveSlide}
            highlightedButtonShape={highlightedButtonShape}
            title={title}
            darkMode={darkMode}
          />

          <CarouselLayout
            {...props}
            selectedSlideIndex={selectedSlideIndex}
            onSelectedSlideChange={handleSlideChange}
          />
        </div>
      </CarouselContainer>
    </section>
  );
}
